'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';

const primary = [
  { key: 'markets', href: '/markets', icon: 'M3 13h4v8H3zM10 5h4v16h-4zM17 9h4v12h-4z' },
  { key: 'watchlist', href: '/watchlist', icon: 'M12 3l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 8.7l5.4-.8z' },
  { key: 'signals', href: '/signals', icon: 'M4 20h16M6 16l4-7 3 4 5-9' },
  { key: 'discover', href: '/discover', icon: 'M12 3l2.5 5.5L20 9.7l-4 4 1 6-5-2.8L7 19.7l1-6-4-4 5.5-1.2z' },
] as const;

const moreItems = [
  { key: 'strategy', href: '/strategy', icon: 'M12 3l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 8.7l5.4-.8z' },
  { key: 'research', href: '/research/spot', icon: 'M4 20h16M6 16l4-7 3 4 5-9' },
  { key: 'news', href: '/news', icon: 'M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1zm2 4h10M7 12h6M7 16h10' },
  { key: 'hyperliquid', href: '/hyperliquid', icon: 'M2 12c2-6 3 6 5 0s3 6 5 0 3 6 5 0' },
  { key: 'portfolio', href: '/portfolio', icon: 'M3 7h18v12H3zM3 11h18M7 7V4h10v3' },
  { key: 'alerts', href: '/alerts', icon: 'M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0' },
  { key: 'notifications', href: '/notifications', icon: 'M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9M10 21h4' },
  { key: 'settings', href: '/settings', icon: 'M12 15a3 3 0 100-6 3 3 0 000 6zm7.4-3a7.4 7.4 0 00-.1-1.2l2-1.5-2-3.5-2.4 1a7.6 7.6 0 00-2-1.2L14.5 3h-4l-.4 2.6a7.6 7.6 0 00-2 1.2l-2.4-1-2 3.5 2 1.5a7.4 7.4 0 000 2.4l-2 1.5 2 3.5 2.4-1a7.6 7.6 0 002 1.2l.4 2.6h4l.4-2.6a7.6 7.6 0 002-1.2l2.4 1 2-3.5-2-1.5c.06-.4.1-.8.1-1.2z' },
  { key: 'help', href: '/help', icon: 'M12 21a9 9 0 100-18 9 9 0 000 18zM9.1 9a3 3 0 015.8 1c0 2-3 2.5-3 4M12 17h.01' },
] as const;

export default function BottomNav() {
  const pathname = usePathname();
  const { language, fullscreenOpen } = useAppStore();
  const t = translate(language);
  const [moreOpen, setMoreOpen] = useState(false);

  // UI09: hide bottom nav on standalone auth pages
  if (pathname.startsWith('/login') || pathname.startsWith('/register')) return null;
  // AUD22: hide while a full-screen form/drawer is open so it never covers submit actions
  if (fullscreenOpen) return null;

  const isActive = (key: string) => {
    if (key === 'markets') return pathname === '/' || pathname.startsWith('/markets') || pathname.startsWith('/coin');
    return pathname.startsWith(`/${key}`);
  };

  const moreActive = moreItems.some((it) => isActive(it.key));

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-gf-border bg-gf-bg/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        <div className="mx-auto flex h-14 max-w-[560px] items-stretch">
          {primary.map((it) => {
            const active = isActive(it.key);
            return (
              <Link
                key={it.key}
                href={it.href}
                className={`flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
                  active ? 'text-gf-accent' : 'text-gf-muted'
                }`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={it.icon} />
                </svg>
                <span>{t.nav[it.key]}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(!moreOpen)}
            className={`flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
              moreOpen || moreActive ? 'text-gf-accent' : 'text-gf-muted'
            }`}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {moreOpen ? (
                <path d="M6 9l6 6 6-6" />
              ) : (
                <path d="M12 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
              )}
            </svg>
            <span>{t.nav.more}</span>
          </button>
        </div>
      </nav>

      {/* More bottom sheet */}
      {moreOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMoreOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-gf-border bg-gf-bg p-4 pb-6">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gf-border" />
            <div className="mx-auto grid max-w-[560px] grid-cols-3 gap-2">
              {moreItems.map((it) => {
                const active = isActive(it.key);
                return (
                  <Link
                    key={it.key}
                    href={it.href}
                    onClick={() => setMoreOpen(false)}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-[11px] transition-colors ${
                      active
                        ? 'border-gf-accent/40 bg-gf-accent/10 text-gf-accent'
                        : 'border-gf-border bg-gf-surface2 text-gf-text hover:bg-white/[0.03]'
                    }`}
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d={it.icon} />
                    </svg>
                    <span>{t.nav[it.key]}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
